import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { errorMessage } from "../../shared/errors";
import { formatCredits, formatDateTime } from "../../shared/format";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import { Alert, Badge, Button, Card, Input, Modal, Spinner } from "../../ui";
import { DeviceActions } from "./DeviceActions";
import { OrgBilling } from "./OrgBilling";
import { Select, StaffAction, stateTone } from "./shared";

export function OrgDetailPage() {
  const { id = "" } = useParams();
  const { role } = useOutletContext<{ role: StaffRole }>();
  const canMutate = roleAtLeast(role, "admin");
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["admin", "org", id],
    queryFn: () => adminApi.org(id),
  });
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [confirm, setConfirm] = useState<{
    title: string;
    action: (reason: string) => Promise<unknown>;
    success: string;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [member, setMember] = useState({ email: "", role: "member" });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "org", id] });
  const run = (fn: () => Promise<unknown>, success: string) =>
    fn()
      .then(() => {
        setMessage({ kind: "success", text: success });
        setConfirm(null);
        setReason("");
        return refresh();
      })
      .catch((err) => {
        setMessage({ kind: "error", text: errorMessage(err) });
        setConfirm(null);
      });

  if (detail.isLoading) return <Spinner />;
  if (!detail.data)
    return (
      <Alert kind="error">
        {errorMessage(detail.error, "Organization not found.")}
      </Alert>
    );
  const d = detail.data;
  const active = d.billing_profile?.billing_active ?? true;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold">{d.org.name}</h1>
          <Badge>{d.org.kind}</Badge>
          <Badge tone={stateTone(d.org.status)}>{d.org.status}</Badge>
          {active ? (
            <Badge tone="success">billing active</Badge>
          ) : (
            <Badge tone="danger">billing suspended</Badge>
          )}
          <div className="ml-auto">
            <StaffAction
              allowed={canMutate}
              variant={active ? "danger" : "secondary"}
              onClick={() =>
                setConfirm(
                  active
                    ? {
                        title: "Suspend billing",
                        action: (r) => adminApi.suspendBilling(id, r),
                        success: "Billing suspended.",
                      }
                    : {
                        title: "Resume billing",
                        action: (r) => adminApi.resumeBilling(id, r),
                        success: "Billing resumed.",
                      },
                )
              }
            >
              {active ? "Suspend billing" : "Resume billing"}
            </StaffAction>
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          id {d.org.id} · created {formatDateTime(d.org.created_at)} · country{" "}
          {d.org.billing_country ?? "—"}
        </p>
        {message ? (
          <div className="mt-3">
            <Alert kind={message.kind}>{message.text}</Alert>
          </div>
        ) : null}
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-semibold">Members</h2>
        <ul className="text-sm">
          {d.members.map((m) => (
            <li key={m.user_id} className="flex items-center gap-2 py-1">
              <Link
                className="text-brand underline"
                to={`/admin/users/${m.user_id}`}
              >
                {m.email}
              </Link>
              <Badge>{m.role}</Badge>
              <span className="ml-auto">
                <StaffAction
                  allowed={canMutate}
                  onClick={() =>
                    run(
                      () => adminApi.removeOrgMember(id, m.user_id),
                      "Member removed.",
                    )
                  }
                >
                  Remove
                </StaffAction>
              </span>
            </li>
          ))}
        </ul>
        {d.org.kind !== "personal" ? (
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            aria-label="Add member"
            onSubmit={(e) => {
              e.preventDefault();
              void run(
                () => adminApi.addOrgMember(id, member),
                "Member added.",
              ).then(() => setMember({ email: "", role: "member" }));
            }}
          >
            <Input
              label="Email"
              value={member.email}
              onChange={(e) => setMember({ ...member, email: e.target.value })}
            />
            <Select
              label="Role"
              value={member.role}
              onChange={(r) => setMember({ ...member, role: r })}
              options={["member", "admin", "owner"].map((r) => ({
                value: r,
                label: r,
              }))}
            />
            <span title={canMutate ? undefined : "Read-only role"}>
              <Button type="submit" disabled={!canMutate || !member.email}>
                Add member
              </Button>
            </span>
          </form>
        ) : null}
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-semibold">Billing</h2>
        <OrgBilling
          orgId={id}
          balances={d.balances}
          profile={d.billing_profile}
          payments={d.payments}
          canMutate={canMutate}
          onChanged={() => void refresh()}
        />
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-semibold">Reservations (held)</h2>
        {d.reservations.length === 0 ? (
          <p className="text-sm text-slate-500">No held reservations.</p>
        ) : (
          <ul className="text-sm">
            {d.reservations.map((r) => (
              <li key={r.id}>
                {r.product} · {formatCredits(r.amount)} · {r.ref ?? "—"} ·
                expires {formatDateTime(r.expires_at)}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-semibold">Devices</h2>
        {d.devices.length === 0 ? (
          <p className="text-sm text-slate-500">No devices enrolled.</p>
        ) : (
          <ul className="text-sm">
            {d.devices.map((device) => (
              <li key={device.id} className="flex items-center gap-2 py-1">
                <span className="font-medium">{device.name}</span>
                <Badge tone={stateTone(device.status)}>{device.status}</Badge>
                <span className="text-xs text-slate-500">
                  last seen {formatDateTime(device.last_seen_at)}
                </span>
                <span className="ml-auto">
                  <DeviceActions
                    device={device}
                    canMutate={canMutate}
                    onAction={(deviceId, action) =>
                      run(
                        () => adminApi.deviceAction(deviceId, action),
                        `Device ${action === "revoke" ? "revoked" : action + "d"}.`,
                      )
                    }
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-semibold">Invitations</h2>
        {d.invitations.length === 0 ? (
          <p className="text-sm text-slate-500">No invitations.</p>
        ) : (
          <ul className="text-sm">
            {d.invitations.map((i) => (
              <li key={i.id}>
                {i.direction} · {i.product} ·{" "}
                <Badge tone={stateTone(i.state)}>{i.state}</Badge> ·{" "}
                {formatCredits(i.reward_credits)} credits
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Modal
        open={confirm !== null}
        title={confirm?.title ?? ""}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                confirm && run(() => confirm.action(reason), confirm.success)
              }
            >
              Confirm
            </Button>
          </>
        }
      >
        <Input
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
